import { useRef, useState } from "react";

import { Button } from "@bokio/designsystem/components/Button";
import { LinkButton } from "@bokio/elements/LinkButton/LinkButton";
import Markdown from "@bokio/elements/Markdown/Markdown";
import { GeneralLangFactory } from "@bokio/lang";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { useActiveUserInfo } from "@bokio/shared/containers/user/withActiveUserInfo";

import type * as m from "@bokio/mobile-web-shared/core/model/model";

import * as styles from "./receiptDescriptionEditor.scss";

interface ReceiptDescriptionEditorProps {
	receipt: m.Accounting.Viewmodels.ReceiptForView | undefined;
	onChanged: () => void;
}

export const ReceiptDescriptionEditor = ({ receipt, onChanged }: ReceiptDescriptionEditorProps) => {
	const [editing, setEditing] = useState<boolean>(false);
	const initialDescription = receipt?.Description ?? "";
	const [currentValue, setCurrentValue] = useState<string>(initialDescription);
	const hasDescription = !!(receipt && receipt.Description);
	const lang = GeneralLangFactory();
	const { companyId } = useActiveUserInfo();
	const [doSave, doSaveRequest] = useLazyApi(
		(newdescription: string, receiptId: string) => {
			return proxy.Accounting.ReceiptController.Update.Put(companyId, {
				ReceiptId: receiptId,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				Receipt: { Description: newdescription } as m.Accounting.Controllers.UpdateReceiptValuesDto,
				Properties: ["Description"],
				TryToAttest: false,
				SaveTemporaryPages: false,
				DeleteReceiptExtraPageIds: [],
			});
		},
		{
			onSuccess: () => {
				setEditing(false);
				onChanged();
			},
		},
	);

	const save = () => {
		if (receipt) {
			doSave(currentValue, receipt.Id);
		}
	};

	const inputRef = useRef<HTMLTextAreaElement | null>(null);

	const toggleEdit = () => {
		if (!receipt || doSaveRequest.isLoading) {
			return;
		}
		const v = !editing;
		setCurrentValue(initialDescription);
		setEditing(v);
		if (v) {
			// set focus on the textarea as soon as we have the reference.
			setTimeout(() => {
				if (inputRef.current) {
					inputRef.current.focus();
				}
			});
		}
	};

	return (
		<div className={styles.container}>
			<div>
				<span className={styles.label}>{lang.Description}</span>{" "}
				<LinkButton testId="ReceiptDescriptionEditor_ToggleEdit" onClick={toggleEdit}>
					{editing ? lang.Cancel : !!currentValue ? lang.Edit : lang.Add}
				</LinkButton>
			</div>
			{editing ? (
				<>
					<textarea
						data-testid="ReceiptDescriptionEditor_DescriptionInput"
						ref={inputRef}
						className={styles.textarea}
						value={currentValue}
						disabled={doSaveRequest.isLoading}
						onChange={e => setCurrentValue(e.currentTarget.value)}
						placeholder={lang.EditDescription_Placeholder}
					></textarea>
					<Button
						testId="ReceiptDescriptionEditor_Submit"
						appearance="secondary"
						loading={doSaveRequest.isLoading}
						onClick={save}
					>
						{hasDescription ? lang.Update : lang.Save}
					</Button>
				</>
			) : (
				<Markdown
					testId="ReceiptDescriptionEditor_DescriptionOutput"
					markdownContent={currentValue}
					deprecatedNativeHtmlElementClassName={styles.description}
					deprecatedOptoutFromTypographicElements
				/>
			)}
		</div>
	);
};
